/**
 * Wider or Narrower — pick which of two positions opens more hands.
 *
 * Pure foundation question: position determines range, later position
 * = wider. The lesson lands fast (a few correct answers is enough to
 * internalize the principle) but the EDGE cases (CO vs SB, BTN vs SB)
 * are where the quiz earns its keep — those are the pairs where rec
 * players guess wrong.
 *
 * Data source: counts computed from `preflopRanges.ts` so this stays
 * pinned to sheet 15. No separate range encoding.
 */

import {
  allHands,
  isInOpenRange,
  POSITIONS,
  type Position,
} from "@/features/quiz/lib/preflopRanges";
import { pickFrom } from "@/features/quiz/lib/common";

/** Number of hands position X opens. Used to compare two positions. */
function rangeSize(pos: Position): number {
  let count = 0;
  for (const hand of allHands()) {
    if (isInOpenRange(pos, hand)) count++;
  }
  return count;
}

/** Pre-computed once — positions are static. */
const RANGE_SIZES: Map<Position, number> = new Map(
  POSITIONS.map((p) => [p, rangeSize(p)]),
);

export interface WiderNarrowerQuestion {
  /** Position A — first option presented. */
  a: Position;
  /** Position B — second option presented. */
  b: Position;
  /** Which position has the wider opening range. */
  wider: Position;
}

export function generateQuestion(
  rand: () => number = Math.random,
): WiderNarrowerQuestion {
  // Keep picking distinct position pairs until we find one with
  // different range sizes. UTG/UTG+1/UTG+2 are size-equal (all EARLY),
  // as are MP/HJ/CO, so same-bucket pairs would be unanswerable.
  // Retry budget is generous; in practice this terminates in 1–3 picks.
  for (let attempt = 0; attempt < 50; attempt++) {
    const a = pickFrom(POSITIONS, rand);
    const b = pickFrom(POSITIONS, rand);
    if (a === b) continue;
    const sa = RANGE_SIZES.get(a)!;
    const sb = RANGE_SIZES.get(b)!;
    if (sa === sb) continue;
    return { a, b, wider: sa > sb ? a : b };
  }
  // Defensive fallback — should never hit given the retry budget.
  return { a: "UTG", b: "BTN", wider: "BTN" };
}

/** Exposed for tests + the route's reveal — explains *why* one is wider. */
export function rangeSizeOf(pos: Position): number {
  return RANGE_SIZES.get(pos)!;
}
